import React, { useEffect } from 'react';
import { Typography, Container, Grid, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Box } from '@mui/material';
import Header from '../../Header/Header';
import AddNewDateModal from './Modals/AddNewDateModal';
import EditTestDateModal from './Modals/EditTestDateModal';
import DeleteTestDateModal from './Modals/DeleteTestDateModal';
import testDateService from '../../../services/TestDateService';
import BasePanel from '../../Common/Layout/BasePanel';
import { BasePanelBox } from '../../Common/Layout/BasePanelBox';
import BaseSheet from '../../Common/Layout/BaseSheet';
import { StyledPanelHeader } from '../../Common/Layout/PanelHeader';
import { CourseProvider } from '../../../context/CourseContext';
import CustomerSelector from '../../Common/CustomerSelector';
import SiteSelector from '../../Common/SiteSelector';
import ProductSelectList from '../../Common/Selectors/ProductSelectList';
import AcademicYearSelector from '../../Common/AcademicYearSelector';
import TestAheadCourseCodeSelector from '../../Common/Selectors/TestAheadCourseCodeSelector';
import { DatePicker } from '../../Common/DatePicker/DatePicker';
import { ProductId, TestDateFilterDTO } from '@beable/roster-backend-client-v1';
import { useTheme } from '@mui/material/styles';
import { getStyles } from './styles';
import { TestDate, TestDateFilter, getUtcDateString } from './TestDate.helper';
import isValid from 'date-fns/isValid';

export default function TestDates() {
    const theme = useTheme();
    const styles = getStyles(theme);
    const [selectedTestDate, setSelectedTestDate] = React.useState<TestDate | undefined>();
    const [shouldOpenAddTestDateModal, setShouldOpenAddTestDateModal] = React.useState(false);
    const [shouldOpenEditTestDateModal, setShouldOpenEditTestDateModal] = React.useState(false);
    const [shouldOpenDeleteTestDateModal, setShouldOpenDeleteTestDateModal] = React.useState(false);
    const [shouldFetchTestDates, setShouldFetchTestDates] = React.useState(true);
    const [testDates, setTestDates] = React.useState<TestDate[]>([]);
    const [siteIdFilter, setSiteIdFilter] = React.useState<number>(0);
    const [customerIdFilter, setCustomerIdFilter] = React.useState<string>('');
    const [productIdFilter, setProductIdFilter] = React.useState<string>('');
    const [academicYearIdFilter, setAcademicYearIdFilter] = React.useState<string>('');
    const [courseCodeFilter, setCourseCodeFilter] = React.useState<string>('');
    const [testDateFilter, setTestDateFilter] = React.useState<string>('');
    const [isOver, setIsOver] = React.useState<boolean>(false);
    
    const openAddTestDateModal = () => setShouldOpenAddTestDateModal(true);
    const closeAddTestDateModal = () => {
        setShouldOpenAddTestDateModal(false);
        setShouldFetchTestDates(!shouldFetchTestDates);
    }
    const openEditTestDateModal = () => setShouldOpenEditTestDateModal(true);
    const closeEditTestDateModal = (testDateHasChange: boolean) => {
        setSelectedTestDate(undefined);
        setShouldOpenEditTestDateModal(false);
        if(testDateHasChange) setShouldFetchTestDates(!shouldFetchTestDates);
    }
    const openDeleteTestDateModal = () => setShouldOpenDeleteTestDateModal(true);
    const closeDeleteTestDateModal = (deleted: boolean) => {
        setShouldOpenDeleteTestDateModal(false);
        setShouldFetchTestDates(!shouldFetchTestDates);
        if (deleted) setSelectedTestDate(undefined);
    }
    const handleTableRowClick = (e: React.MouseEvent<HTMLTableRowElement>) => {
        if (e) {
            e.persist();
        }
        e.stopPropagation();
        const selectedRow = e.currentTarget;
        const newSelection = testDates.find(testDate => testDate.id!.toString() === selectedRow.id);
        
        if (newSelection && newSelection.id !== selectedTestDate?.id) {
            setSelectedTestDate(newSelection);
        } else { //clear selection
            setSelectedTestDate(undefined);
        }
    }

    const handleDateChange = (date: Date) => {
        if (!isValid(date)) {
            return;
        }

        const testDate = getUtcDateString(date, 'yyyy-MM-dd');
        setTestDateFilter(testDate);
    }

    const clearTestDatesFilter = () => {
        setProductIdFilter('');
        setAcademicYearIdFilter('');
        setCourseCodeFilter('');
        setTestDateFilter('');
        setIsOver(false);
        // fetchTestDates(defaultTestDateFilter);
    }

    const hasFilter = (): boolean => {
        return !!(productIdFilter || academicYearIdFilter || courseCodeFilter || testDateFilter);
    }

    async function fetchTestDates(filter: TestDateFilter) {
        const testDates = await testDateService.getAllTestDates(filter as TestDateFilterDTO);  
        setTestDates(testDates || []);
    }

    useEffect(() => {
        const filter = {
            siteId: siteIdFilter,
            customerId: customerIdFilter,
            productId: productIdFilter,
            academicYearId: academicYearIdFilter,
            courseCode: courseCodeFilter,
            testDate: testDateFilter,
        };
        fetchTestDates(filter);
    }, [shouldFetchTestDates, siteIdFilter, customerIdFilter, productIdFilter, academicYearIdFilter, courseCodeFilter, testDateFilter]);

    return (
        <CourseProvider>
            <Header />
            <Grid container>
            <BasePanel>
                <BasePanelBox>
                <StyledPanelHeader header="Test Dates" />
                <Grid container sx={{ marginBottom: '28px' }} size={12}>
                    <Typography variant="h5" sx={{ marginLeft: '16px' }}>Filter Options</Typography>
                    <CustomerSelector
                        onSelect={(customer: any) => { setCustomerIdFilter(customer?.id)} }
                    />
                    <SiteSelector
                        customerId={customerIdFilter}
                        onSelect={(site: any) => { setSiteIdFilter(site?.id)} }
                        disabled={!customerIdFilter}
                    />
                    <ProductSelectList
                        customerId={customerIdFilter}
                        siteId={siteIdFilter}
                        productId={ProductId.Exampal}
                        onChange={(product: any) => {
                            // This block must be changed when we have other products than Exampal for Test dates
                            if(!product || (product.id && product.id !== ProductId.Exampal)) {
                                setProductIdFilter('');
                            } else {
                                setProductIdFilter(ProductId.Exampal);
                            }
                            // setProductIdFilter(product?.id)
                        } }
                        disabled
                    />
                    <AcademicYearSelector
                        clearAcademicYear={!academicYearIdFilter}
                        siteId={siteIdFilter}
                        handleSelection={(academicYear: any) => { setAcademicYearIdFilter(academicYear?.academicYearId); } }
                    />
                    <TestAheadCourseCodeSelector
                        courseCode={courseCodeFilter}
                        onChange={(courseCode: any) => { setCourseCodeFilter(courseCode); } }
                    />
                    <Box sx={[styles.pFormControl, { boxSizing: 'border-box' }]}>
                        <DatePicker
                            label='Test Date'
                            value={testDateFilter ? new Date(getUtcDateString(testDateFilter)) : null}
                            onChange={handleDateChange}
                            minDate={new Date('2020-01-02')}
                        />
                    </Box>
                    { hasFilter() ? 
                        <Button  onMouseOver={() => setIsOver(true) } onMouseOut={() => setIsOver(false) } sx={styles.verticalBtn} variant="contained" color="primary" onClick={clearTestDatesFilter} >
                            Clear Fields
                        </Button> 
                        : undefined 
                    }
                </Grid>
                <Grid container size={12}>
                    <Typography variant="h5" sx={{ marginLeft: '16px' }}>General Options</Typography>
                    <Container sx={styles.dfb}>
                        <Button sx={styles.verticalBtn} variant="contained" color="primary" onClick={openAddTestDateModal}>Add new test date</Button>
                    </Container>
                    {selectedTestDate?.testDate ? (
                        <>
                            <Typography variant="h5" sx={{ marginLeft: '16px' }}>Options</Typography>
                            <Container sx={styles.dfb}>
                                <Button sx={styles.verticalBtn} variant="contained" color="secondary" onClick={openEditTestDateModal}>Edit Test Date</Button>
                                {selectedTestDate.id ? <Button
                                    sx={styles.verticalBtn}
                                    variant="contained"
                                    onClick={openDeleteTestDateModal}>Delete Test Date</Button> : undefined}
                            </Container>
                        </>
                    ) : undefined}
                </Grid>
                </BasePanelBox>
            </BasePanel>
            <BaseSheet maxWidth='lg' widerPaper={true}>
                <Grid size={12}>
                    <Typography align-left='true' sx={{
                        marginBottom: "16px"
                    }}>Use this page to manage test dates.</Typography>
                    <Container sx={{ ...styles.dfb, padding: 0 }}>
                        <TableContainer component={Paper}>
                            <Table sx={styles.table} aria-label="admin data table">
                                <TableHead>
                                    <TableRow>
                                        <TableCell align="left">Site</TableCell>
                                        <TableCell align="left">Customer</TableCell>
                                        <TableCell align="left">Product</TableCell>
                                        <TableCell align="left">School Year</TableCell>
                                        <TableCell align="left">Course Code</TableCell>
                                        <TableCell align="left">Test Date</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {testDates.map(testDate => {
                                        return (
                                            <TableRow sx={selectedTestDate?.id === testDate.id ? styles.highlight : undefined} id={testDate.id} key={testDate.id} data-testid={testDate.id} onClick={handleTableRowClick}>
                                                <TableCell align="left">{testDate.siteSubdomain}</TableCell>
                                                <TableCell align="left">{testDate.customerName}</TableCell>
                                                <TableCell align="left">{testDate.productName}</TableCell>
                                                <TableCell align="left">{testDate.schoolYear}</TableCell>
                                                <TableCell align="left">{testDate.courseCode}</TableCell>
                                                <TableCell align="left">{`${testDate.testDate}`}</TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Container>
                </Grid>
            </BaseSheet>
            </Grid >
            {shouldOpenAddTestDateModal && <AddNewDateModal open={shouldOpenAddTestDateModal} handleClose={closeAddTestDateModal} />}
            {shouldOpenEditTestDateModal ? <EditTestDateModal open={shouldOpenEditTestDateModal} handleClose={closeEditTestDateModal} testDate={selectedTestDate!} /> : undefined}
            {shouldOpenDeleteTestDateModal ? <DeleteTestDateModal open={shouldOpenDeleteTestDateModal} handleClose={closeDeleteTestDateModal} testDate={selectedTestDate!} /> : undefined}
        </CourseProvider>
    );
}